import { checkNoValueInObj, resetObjFields, capitalize, typeCheck } from '@/utils';

const setErrorsObj = (fieldsObj, ignoredFieldsArr) => {
  return Object.keys(fieldsObj).reduce((acc, key) => {
    if (!acc['count']) acc['count'] = 0;
    if (!ignoredFieldsArr.includes(key)) acc[key] = '';
    return acc;
  }, {});
};

export const formMixin = (fields, ignored = [], shallowCheck = [], customErrors = []) => ({
  data() {
    return {
      fieldsData: { ...fields },
      errors: {},
      formLoading: false,
      ignoredFields: ignored,
    };
  },
  watch: {
    // Update errors obj to reflect changes in the ignoredFields array
    ignoredFields(ignored) {
      this.errors = setErrorsObj(fields, ignored);
    },
  },
  methods: {
    handleSubmit(fn) {
      return async e => {
        if (e) e.preventDefault();

        this.formLoading = true;

        // set errors
        this.handleSetErrors();

        // set custom errors
        this.handleSetCustomErrors();

        // quit before calling the api in case we have pending errors
        if (this.errors.count) {
          this.formLoading = false;
          return false;
        }

        // pass in the fields data to the closure
        await fn(this.fieldsData);

        this.formLoading = false;

        return true;
      };
    },
    handleSetField(name, value, keyIdentifier) {
      this.errors[name] && this.handleResetError(name); // reset the error on new value
      typeCheck(this.fieldsData[name]).isArray
        ? (this.fieldsData[name] = this.handleArrayDataField(
            this.fieldsData[name],
            value,
            keyIdentifier,
          ))
        : (this.fieldsData[name] = value);
    },
    handleArrayDataField(array, data, keyIdentifier) {
      // If exiting item we replace the item in array data else we append the new item.
      // We account for the case in which we have an array of objects or an array of primitives.
      const existingItem = array.find(i =>
        keyIdentifier ? i[keyIdentifier] === data[keyIdentifier] : data === i,
      );
      return existingItem
        ? array.map(item =>
            keyIdentifier
              ? item[keyIdentifier] === existingItem[keyIdentifier]
                ? data
                : item
              : item === data
              ? data
              : item,
          )
        : array.concat(data);
    },
    handleResetFields() {
      this.fieldsData = resetObjFields(this.fieldsData, shallowCheck);
    },
    handleResetField(name) {
      this.fieldsData = { ...this.fieldsData, [name]: '' };
    },
    handleSetErrors() {
      const missingFields = checkNoValueInObj(this.fieldsData, this.ignoredFields, shallowCheck);

      const errors = missingFields.reduce(
        (acc, error) => {
          const field = error
            .split('_')
            .map(w => capitalize(w))
            .join(' ');
          acc[error] = `${field} is required.`;
          !acc['count']
            ? (acc['count'] = 1)
            : acc['count'] !== missingFields.length && (acc['count'] += 1);
          return acc;
        },
        { ...this.errors },
      );

      this.errors = errors;

      return errors;
    },
    handleSetCustomErrors() {
      customErrors.forEach(
        ({ field, msg, predicateFn }) =>
          this.fieldsData[field] &&
          predicateFn(this.fieldsData[field]) &&
          this.handleSetError(field, msg),
      );
    },
    handleSetError(field, message) {
      if (this.errors[field]) return (this.errors = { ...this.errors, [field]: message });
      return (this.errors = {
        ...this.errors,
        [field]: message,
        count: (this.errors['count'] += 1),
      });
    },
    handleResetErrors() {
      this.errors = resetObjFields(this.errors, shallowCheck);
    },
    handleResetError(error) {
      if (this.errors[error]) {
        this.errors[error] = '';
        this.errors['count'] -= 1;
      }
    },
  },
  mounted() {
    this.errors = setErrorsObj(fields, this.ignoredFields);
  },
});
